import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { HttpStatusCode } from '@thesavingsgroup/enums/HttpStatusCode';
import i18n from 'i18next';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { StateCreator } from 'zustand';

import { DeliverPinLocation } from '../pinStore/enums/DeliverPinLocation';
import { DeliverPinParams } from '../pinStore/types/DeliverPinParams';
import { UserExistsExceptionCode } from './enums/UserExistsExceptionCode';
import { User } from './types/User';
import { UserState } from './types/UserState';
import { UserStateDependencies } from './types/UserStateDependencies';

export const keysToPersist = ['pinPurpose'];

export const userStore =
  ({
    useAnalyticStore,
    useApiStore,
    useAttributionStore,
    useLeadChannelStore,
    useParameterStore,
  }: UserStateDependencies): StateCreator<UserState> =>
  (set, get) => ({
    fetchCurrentUserFailed: false,
    isUpdatingUser: false,
    pinPurpose: null,
    user: null,
    userAuthenticateFailedMessage: null,
    userAuthenticateLocked: false,
    userAuthenticateSuccess: false,
    userCreateFailedMessage: null,
    userCreateSuccess: false,
    userExistsException: null,
    userUpdateFailedMessage: null,
    userUpdateSuccess: false,

    computed: {
      get hasCurrentUser(): boolean {
        return !isEmpty(get().user);
      },
      get isPinPurposeEmpty(): boolean {
        return isEmpty(get().pinPurpose);
      },
      get shouldCreateUser(): boolean {
        return get().pinPurpose === AuthType.ACCOUNT_REGISTRATION_USING_PIN;
      },
      get shouldUpdatePhone(): boolean {
        return get().pinPurpose === AuthType.EDIT_PHONE_USING_PIN;
      },
      get userEmailAddress(): string {
        const user = get().user;
        const { primaryEmailAddress = '' } = user || {};

        return primaryEmailAddress;
      },
      get userFirstName(): string {
        const user = get().user;
        const { firstName = '' } = user || {};

        return firstName;
      },
      get userLastName(): string {
        const user = get().user;
        const { lastName = '' } = user || {};

        return lastName;
      },
      get userPhoneNumber(): string {
        const user = get().user;
        const { primaryPhoneNumber = '' } = user || {};

        return primaryPhoneNumber;
      },
      get userTruncatedName(): string {
        const user = get().user;
        const { firstName = '', lastName = '' } = user || {};

        return `${firstName} ${lastName.charAt(0)}.`;
      },
    },

    clearPinPurpose: (): void => {
      set({ pinPurpose: null });
    },
    clearUserUpdate: (): void => {
      set({
        isUpdatingUser: false,
        userUpdateFailedMessage: null,
        userUpdateSuccess: false,
      });
    },
    deriveDeliverPinParams(
      userExistsException: UserExistsExceptionCode,
      deliverPinLocation: DeliverPinLocation,
      primaryPhoneNumber: string,
      primaryEmailAddress: string,
    ): DeliverPinParams {
      /*
        Determines the auth intent from the userExistsException, persists it,
        and builds the params needed to deliver the PIN:
        - BOTH_NOT_FOUND            - create a new account
        - EMAIL_ADDRESS_REGISTERED  - update the phone number on an existing account
        - anything else             - sign in to an existing account

        For more information, see: https://savingsgroup.atlassian.net/wiki/x/AQBvRQ
      */
      const { getDeliverPinParams } = get();

      const pinPurpose =
        userExistsException === UserExistsExceptionCode.BOTH_NOT_FOUND
          ? AuthType.ACCOUNT_REGISTRATION_USING_PIN
          : userExistsException ===
            UserExistsExceptionCode.EMAIL_ADDRESS_REGISTERED
          ? AuthType.EDIT_PHONE_USING_PIN
          : AuthType.ACCOUNT_SIGN_IN_USING_PIN;

      set({ pinPurpose });

      return getDeliverPinParams(
        deliverPinLocation,
        primaryPhoneNumber,
        primaryEmailAddress,
      );
    },
    fetchCurrentUser: (): Promise<void> => {
      const {
        computed: { hasCurrentUser },
      } = get();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({ fetchCurrentUserFailed: false });

      return portalHttpRequest
        .get<User>('/2.0/users/current', {
          // if the user is already signed in and we get a 401, their session has
          // expired and we should sign them out. if no user is loaded yet (ex: initial
          // page load while signed out), we skip the sign-out redirect logic found in apiStore.
          suppressUnauthorizedRedirect: !hasCurrentUser,
        })
        .then((user): void => {
          set({ user });
        })
        .catch((): void => {
          // current user is being checked for on all routes aside from /sign-out.
          // if the user is unable to be fetched in any case, we reset the user state
          // to ensure the user cannot access an authenticated route in the SessionGuard.
          set({
            fetchCurrentUserFailed: true,
            user: null,
          });
        });
    },
    getDeliverPinParams(
      deliverPinLocation: DeliverPinLocation,
      primaryPhoneNumber: string,
      primaryEmailAddress: string,
    ): DeliverPinParams {
      const {
        computed: { shouldUpdatePhone },
        pinPurpose,
      } = get();

      return {
        // When updating phone number, we should provide the email
        // as it's needed in the api to identify the existing user account.
        ...(shouldUpdatePhone && { email: primaryEmailAddress }),
        deliverPinLocation,
        phone: primaryPhoneNumber,
        purpose: pinPurpose,
      };
    },
    setUserFirstName: (value: string): void => {
      set({ user: { ...get().user, firstName: value } });
    },
    setUserLastName: (value: string): void => {
      set({ user: { ...get().user, lastName: value } });
    },
    userAgreeToTerms: (vehicleId: string): Promise<void> => {
      if (!vehicleId) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest.post('/2.0/accepted-terms-of-use', {
        vehicleId,
      });
    },
    userAuthenticate: (
      primaryPhoneNumber: string,
      pin: string,
      leadChannelCode: string | null | undefined,
      purpose: AuthType,
      autopayNumber?: string,
    ): Promise<void> => {
      set({
        userAuthenticateFailedMessage: null,
        userAuthenticateLocked: false,
        userAuthenticateSuccess: false,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        computed: { attributionsForLeadChannel },
      } = useAttributionStore.getState();

      const { vehicleId } = useParameterStore.getState();
      const {
        sendUserActivatedAccountAnalytics,
        sendUserActivatedApplicationAnalytics,
        sendUserAuthenticatedAnalytics,
      } = useAnalyticStore.getState();

      return portalHttpRequest
        .post<{ user: User }>(
          '/2.0/session',
          {
            attributions: attributionsForLeadChannel,
            autopayNumber,
            includeUser: true,
            leadChannelCode,
            pin,
            primaryPhoneNumber,
            purpose,
            vehicleId,
          },
          {
            // we skip the sign-out redirect logic found in apiStore when a user tries to
            // authenticate because if the authentication fails with a 401, we want to show
            // an error message instead of redirecting them.
            suppressUnauthorizedRedirect: true,
          },
        )
        .then(({ user }): void => {
          if (purpose === AuthType.ACCOUNT_ACTIVATION_USING_PIN) {
            sendUserActivatedAccountAnalytics();
          }

          if (purpose === AuthType.APPLICATION_ACTIVATION_USING_PIN) {
            sendUserActivatedApplicationAnalytics();
          }

          if (purpose === AuthType.ACCOUNT_SIGN_IN_USING_PIN) {
            sendUserAuthenticatedAnalytics();
          }

          set({ user, userAuthenticateSuccess: true });
        })
        .catch((error: unknown): void => {
          set({
            userAuthenticateFailedMessage: lodashGet(
              i18n.t('errors.userAuthenticate.statusMessage'),
              toString(lodashGet(error as any, 'response.status')),
              i18n.t('errors.userAuthenticate.defaultMessage'),
            ),
            userAuthenticateLocked:
              lodashGet(error as any, 'response.status') ===
              HttpStatusCode.FORBIDDEN,
          });
        });
    },
    userCreate: (
      firstName: string,
      lastName: string,
      primaryEmailAddress: string,
      primaryPhoneNumber: string,
      pin: string,
    ): Promise<void> => {
      set({
        userCreateFailedMessage: null,
        userCreateSuccess: false,
        userExistsException: null,
      });

      const {
        computed: { isValidLeadChannel },
      } = useLeadChannelStore.getState();

      const { leadChannelCode } = useParameterStore.getState();
      const { sendUserRegisteredAnalytics } = useAnalyticStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      const {
        computed: { attributionsForLeadChannel },
      } = useAttributionStore.getState();

      return portalHttpRequest
        .post<{ user: User }>(
          '/2.0/users',
          {
            attributions: attributionsForLeadChannel,
            firstName,
            lastName,
            ...(isValidLeadChannel && { leadChannelCode }),
            pin,
            primaryEmailAddress,
            primaryPhoneNumber,
          },
          {
            // we skip the sign-out redirect logic found in apiStore when a user tries to
            // create an account because if the create fails with a 401, we want to show
            // an error message instead of redirecting them.
            suppressUnauthorizedRedirect: true,
          },
        )
        .then(({ user }): void => {
          sendUserRegisteredAnalytics();

          set({ user, userCreateSuccess: true });
        })
        .catch((error: unknown): void => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          if (errorCode in UserExistsExceptionCode) {
            set({ userExistsException: errorCode });
          } else {
            set({
              userCreateFailedMessage: lodashGet(
                i18n.t('errors.userCreate.statusMessage'),
                toString(lodashGet(error as any, 'response.status')),
                i18n.t('errors.userCreate.defaultMessage'),
              ),
            });
          }
        });
    },
    userExists: (
      primaryPhoneNumber: string,
      primaryEmailAddress: string,
    ): Promise<void> => {
      set({ userExistsException: null });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get(`/2.0/users`, {
          params: {
            primaryEmailAddress,
            primaryPhoneNumber,
          },
        })
        .catch((error: unknown): void => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          set({ userExistsException: errorCode });
        });
    },
    userUpdate: (): Promise<void> => {
      const {
        computed: { userFirstName: firstName, userLastName: lastName },
      } = get();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        isUpdatingUser: true,
        userUpdateFailedMessage: null,
        userUpdateSuccess: false,
      });

      return portalHttpRequest
        .patch<User>('/2.0/users/current', {
          firstName,
          lastName,
        })
        .then((user): void => {
          set({ user, userUpdateSuccess: true });
        })
        .catch((error: unknown): void => {
          const errorCode = lodashGet(
            error,
            'response.data._embedded.errors.0.errorCode',
            null,
          );

          if (errorCode in UserExistsExceptionCode) {
            set({ userExistsException: errorCode });
          } else {
            set({
              userUpdateFailedMessage: i18n.t(
                'errors.userUpdate.defaultMessage',
              ),
            });
          }
        })
        .finally((): void => {
          set({ isUpdatingUser: false });
        });
    },
  });
